import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Heart } from "lucide-react";
import { getSocket } from "@/lib/socket";
import { useAuth } from "@/contexts/AuthContext";
import { usePlatformAuth } from "@/contexts/PlatformAuthContext";

const fmtUsd = (cents) => {
  const n = (cents || 0) / 100;
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
};

// Global floating live banners (01-home.md "Floating Elements", tipping.md).
// - Live Activity Banner: "X just subscribed" (subscriber-joined), non-subscribers only.
// - Live Tip Banner: "X just tipped $Y" / "Anonymous user just tipped $Y" (tip-received), everyone.
// Both auto-dismiss after 3s and reset the timer on every new event.
export const LiveActivityBanners = () => {
  const { user } = useAuth();
  const { isSubscriber } = usePlatformAuth();
  const [sub, setSub] = useState(null);
  const [tip, setTip] = useState(null);
  const subTimer = useRef(null);
  const tipTimer = useRef(null);

  const viewerIsSubscriber = isSubscriber || (user && user.subscription_status === "active");

  useEffect(() => {
    const socket = getSocket();
    const onJoin = (data) => {
      if (viewerIsSubscriber) return; // non-subscribers only
      setSub({ name: data.first_name || "Someone", id: Date.now() });
      if (subTimer.current) clearTimeout(subTimer.current);
      subTimer.current = setTimeout(() => setSub(null), 3000);
    };
    const onTip = (data) => {
      const amt = fmtUsd(data.amount_cents);
      const text = data.is_anonymous
        ? `Anonymous user just tipped ${amt}`
        : `${data.first_name || "Someone"} just tipped ${amt}`;
      setTip({ text, id: Date.now() });
      if (tipTimer.current) clearTimeout(tipTimer.current);
      tipTimer.current = setTimeout(() => setTip(null), 3000);
    };
    socket.on("subscriber-joined", onJoin);
    socket.on("tip-received", onTip);
    return () => {
      socket.off("subscriber-joined", onJoin);
      socket.off("tip-received", onTip);
      if (subTimer.current) clearTimeout(subTimer.current);
      if (tipTimer.current) clearTimeout(tipTimer.current);
    };
  }, [viewerIsSubscriber]);

  const enter = { initial: { opacity: 0, x: 24, y: -6 }, animate: { opacity: 1, x: 0, y: 0 }, exit: { opacity: 0, x: 24 }, transition: { duration: 0.28 } };

  return (
    <div className="fixed top-20 right-4 z-[75] flex flex-col items-end gap-2 pointer-events-none" data-testid="live-activity-banners">
      <AnimatePresence>
        {sub && !viewerIsSubscriber && (
          <motion.div
            key="sub"
            {...enter}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium shadow-lg"
            style={{ background: "var(--accent-light)", color: "var(--accent-primary)", border: "1px solid var(--accent-primary)" }}
            data-testid="live-subscriber-toast"
          >
            <Sparkles size={14} /> {sub.name} just subscribed
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {tip && (
          <motion.div
            key="tip"
            {...enter}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold shadow-lg metallic-sheen"
            style={{ color: "#fff" }}
            data-testid="live-tip-banner"
          >
            <Heart size={14} fill="#fff" /> {tip.text}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LiveActivityBanners;
